import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import styles from './WorkDistrict.module.css';

interface RewardOption {
  rounds: number;
  silverReward: number;
  reputationReward: number;
}

interface NPC {
  id: number;
  name: string;
  description: string;
  rewardOptions: RewardOption[];
}

interface MultiplierData {
  rank: number;
  multiplier: number;
  hint: string | null;
}

const WorkDistrict: React.FC = () => {
  const [npcs, setNpcs] = useState<NPC[]>([]);
  const [selectedNpc, setSelectedNpc] = useState<NPC | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [multiplierData, setMultiplierData] = useState<MultiplierData | null>(null);
  const { mercenaryId, mercenary } = useMercenary();

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  useEffect(() => {
    fetchNpcs();
  }, []);

  useEffect(() => {
    if (!mercenaryId) return;
    axios
      .get(`/api/work/multiplier?mercenaryId=${mercenaryId}`, { withCredentials: true })
      .then((res) => setMultiplierData(res.data))
      .catch(() => setMultiplierData(null));
  }, [mercenaryId]);

  const fetchNpcs = () => {
    axios
      .get('/api/work/npcs', { withCredentials: true })
      .then((response) => setNpcs(response.data))
      .catch((error) => {
        console.error('Error fetching NPCs:', error);
        displayToast('Failed to load NPCs. Please try again later.', 'error');
      });
  };

  const handleNpcSelect = (npc: NPC) => {
    setSelectedNpc(npc);
    setSelectedOptionIndex(0); // Reset to first option
  };

  const handleWork = () => {
    if (selectedNpc) {
      const option = selectedNpc.rewardOptions[selectedOptionIndex];
      if (!option) return;

      axios
        .post('/api/work/earn', { npcId: selectedNpc.id, rounds: option.rounds, mercenaryId }, { withCredentials: true })
        .then((res) => {
            const { silverEarned, hint } = res.data;
            const hintText = hint ? ` (${hint})` : '';
            displayToast(`Contract fulfilled! Earned ${silverEarned} Silver${hintText}.`, 'success');
        })
        .catch((error) => {
          console.error('Failed to earn silver:', error);
          const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to complete contract. Please try again later.';
          displayToast(msg, 'error');
        });
    }
  };

  // Safe access to current option
  const currentOption = selectedNpc && selectedNpc.rewardOptions[selectedOptionIndex];
  const availableRounds = mercenary?.rounds ?? 0;
  const canAfford = currentOption ? availableRounds >= currentOption.rounds : false;
  const multiplier = multiplierData?.multiplier ?? 1;
  const adjustedSilver = currentOption ? Math.floor(currentOption.silverReward * multiplier) : 0;

  return (
    <div className={styles.workDistrictContainer}>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}
      
      <div className={styles.headerSection}>
        <h1 className={styles.workTitle}>Work District</h1>
        <div className={styles.subTitle}>Where sweat turns to silver</div>
      </div>

      {!selectedNpc ? (
          <div className={styles.jobBoard}>
          {npcs.map((npc) => (
            <div key={npc.id} className={styles.jobPost}>
              <div className={styles.postHeader}>
                <h3 className={styles.npcName}>{npc.name}</h3>
              </div>
              <div className={styles.postBody}>
                "{npc.description}"
              </div>
              <button onClick={() => handleNpcSelect(npc)} className={styles.viewContractBtn}>
                 View Contracts
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.contractContainer}>
            <div className={styles.contractHeader}>
                <h2 className={styles.contractTitle}>Labor Contract</h2>
                <button className={styles.closeBtn} onClick={() => setSelectedNpc(null)}>&times;</button>
            </div>

            <div className={styles.contractDetails}>
                <div style={{ fontStyle: 'italic', marginBottom: '10px' }}>
                    Contractor: <strong>{selectedNpc.name}</strong>
                    <br/>
                    Currently evaluating your stamina...
                </div>

                {selectedNpc.rewardOptions.length > 0 && currentOption ? (
                    <div className={styles.rewardSection}>
                        <div className={styles.sliderContainer}>
                            <label className={styles.sliderLabel}>
                                Work Duration: <span style={{color: '#fff', fontWeight: 'bold'}}>{currentOption.rounds} Rounds</span>
                            </label>
                            <input 
                                type="range" 
                                min="0" 
                                max={selectedNpc.rewardOptions.length - 1} 
                                value={selectedOptionIndex} 
                                onChange={(e) => setSelectedOptionIndex(Number(e.target.value))}
                                className={styles.workSlider}
                            />
                            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#aaa'}}>
                                <span>Short Task</span>
                                <span>Long Haul</span>
                            </div>
                        </div>

                        <div className={styles.rewardsPreview}>
                            <div className={styles.rewardItem}>
                                <img src="/img/icons/silver_coin.png" alt="Silver" width="24" onError={(e:any) => e.target.style.display='none'} />
                                <span className={styles.rewardValue}>{adjustedSilver} Silver</span>
                            </div>
                            <div className={styles.rewardItem}>
                                <span>🏆</span>
                                <span className={styles.rewardValue}>{currentOption.reputationReward} Rep</span>
                            </div>
                        </div>
                        {multiplierData?.hint && (
                            <div className={styles.incomeHint}>
                                ⚠️ {multiplierData.hint}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>No work available.</div>
                )}
                
                <button 
                    onClick={handleWork} 
                    disabled={!canAfford} 
                    className={styles.signContractBtn}
                    title={!canAfford ? `Not enough rounds (have ${availableRounds}, need ${currentOption?.rounds})` : "Sign Contract"}
                >
                    {canAfford ? "Sign Contract & Work" : "Not Enough Rounds"}
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default WorkDistrict;
